/** Client-side mirror of server coinbasePortfolioHistory return math. */

function coinbaseOpeningEquity(series, currentNav = null, netContributed = null) {
  if (!Array.isArray(series) || !series.length) return 0;

  const first = Number(series[0]?.value) || 0;
  const last = currentNav != null ? currentNav : Number(series[series.length - 1]?.value) || 0;
  const contributed = Number(netContributed);
  const saneCap = Math.max(last * 25, Number.isFinite(contributed) && contributed > 0 ? contributed * 3 : 0, 100);

  if (first > 0 && first <= saneCap) return first;

  if (Number.isFinite(contributed) && contributed > 0 && contributed <= saneCap) {
    return contributed;
  }

  for (const point of series) {
    const value = Number(point.value) || 0;
    if (value > 0 && value <= saneCap) return value;
  }

  return first;
}

function coinbaseMetricsSeries(series, currentNav = null, netContributed = null) {
  if (!Array.isArray(series) || series.length < 2) return series;

  const opening = coinbaseOpeningEquity(series, currentNav, netContributed);
  const first = Number(series[0]?.value) || 0;
  if (Math.abs(opening - first) < 0.01) return series;

  const startIdx = series.findIndex((point) => {
    const value = Number(point.value) || 0;
    return Math.abs(value - opening) < 0.01;
  });

  if (startIdx <= 0) {
    return [{ ...series[0], value: opening }, ...series.slice(1)];
  }

  return series.slice(startIdx);
}

function coinbaseOpeningBasis(series, change, last, netContributed = null) {
  const basis = last - change;
  if (Number.isFinite(basis) && basis > 0) return basis;

  const opening = coinbaseOpeningEquity(series, last, netContributed);
  if (opening > 0) return opening;
  return basis;
}

/**
 * Flows after the opening point. The first point's equity already includes its
 * own deposit, so counting that flow again double-charges it.
 */
function flowsAfterOpening(series) {
  return series
    .slice(1)
    .reduce((sum, point) => sum + (Number(point.netFlow) || 0), 0);
}

export function coinbasePortfolioReturnPct(series, currentNav = null, netContributed = null) {
  if (!Array.isArray(series) || series.length < 2) return null;

  const metricsSeries = coinbaseMetricsSeries(series, currentNav, netContributed);
  const first = Number(metricsSeries[0]?.value) || 0;
  const last = currentNav != null ? currentNav : Number(metricsSeries[metricsSeries.length - 1]?.value) || 0;
  const change = last - first - flowsAfterOpening(metricsSeries);
  const basis = coinbaseOpeningBasis(metricsSeries, change, last, netContributed);

  if (Number.isFinite(basis) && basis > 0) return (change / basis) * 100;
  return null;
}

export function coinbasePortfolioChange(series, currentNav = null, netContributed = null) {
  if (!Array.isArray(series) || series.length < 2) return null;

  const metricsSeries = coinbaseMetricsSeries(series, currentNav, netContributed);
  const first = Number(metricsSeries[0]?.value) || 0;
  const last = currentNav != null ? currentNav : Number(metricsSeries[metricsSeries.length - 1]?.value) || 0;
  return last - first - flowsAfterOpening(metricsSeries);
}

export function equityMaxDrawdownPct(series) {
  if (!Array.isArray(series) || !series.length) return null;

  let peak = Number(series[0].value) || 0;
  let maxDrawdown = 0;

  for (const point of series) {
    const value = Number(point.value) || 0;
    if (value > peak) peak = value;
    if (peak > 0) {
      const dd = (value - peak) / peak;
      if (dd < maxDrawdown) maxDrawdown = dd;
    }
  }

  return Math.abs(maxDrawdown * 100);
}

export function trailingCoinbaseReturn(series, days, currentNav = null, netContributed = null) {
  if (!Array.isArray(series) || series.length < 2) return null;
  const last = new Date(series[series.length - 1].date).getTime();
  const cutoff = last - days * 86400000;
  const window = series.filter((point) => new Date(point.date).getTime() >= cutoff);
  return coinbasePortfolioReturnPct(window, currentNav, netContributed);
}

export function isCoinbaseBroker(broker) {
  return String(broker || '').toLowerCase().includes('coinbase');
}

export function computeCoinbaseYouMetrics(points, { realizedProfit = null, netContributed = null } = {}) {
  const currentNav = Number(points[points.length - 1]?.value);
  const nav = Number.isFinite(currentNav) ? currentNav : null;

  // Mirrors the server: flow-adjusted change is the money-weighted P&L, with
  // realized trade P&L only as a fallback. Every metric reads the same cleaned
  // series so gain and drawdown can't disagree about the opening value.
  const metricsSeries = coinbaseMetricsSeries(points, nav, netContributed);
  const change = coinbasePortfolioChange(metricsSeries, nav, netContributed);

  return {
    gain: coinbasePortfolioReturnPct(metricsSeries, nav, netContributed),
    monthly: trailingCoinbaseReturn(metricsSeries, 30, nav, netContributed),
    yearly: trailingCoinbaseReturn(metricsSeries, 365, nav, netContributed),
    drawdown: equityMaxDrawdownPct(metricsSeries),
    profit: change != null ? change : realizedProfit,
  };
}
